// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { DocumentScope } from '@shared/data-access/types/engine-types';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { DocumentScopeTab } from '../types/document-filters';

export interface DocumentCategory {
  readonly id: string;
  readonly subcategories: readonly string[];
}

export interface DocumentCatalogOption {
  readonly value: string;
  readonly label: string;
}

export const useDocumentCatalog = () => {
  const { t } = useTranslation();

  const categoryLabels = useMemo(() => {
    const value = t('documents.catalog.categories', {
      ns: 'components',
      returnObjects: true,
    });

    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return {} as Record<string, string>;
    }

    return value as Record<string, string>;
  }, [t]);

  const subcategoryLabels = useMemo(() => {
    const value = t('documents.catalog.subcategories', {
      ns: 'components',
      returnObjects: true,
    });

    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return {} as Record<string, string>;
    }

    return value as Record<string, string>;
  }, [t]);

  const currencyLabels = useMemo(() => {
    const value = t('documents.catalog.currencies', {
      ns: 'components',
      returnObjects: true,
    });

    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return {} as Record<string, string>;
    }

    return value as Record<string, string>;
  }, [t]);

  const languageLabels = useMemo(() => {
    const value = t('documents.catalog.languages', {
      ns: 'components',
      returnObjects: true,
    });

    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return {} as Record<string, string>;
    }

    return value as Record<string, string>;
  }, [t]);

  const getCategoriesForScope = useCallback(
    (scope: DocumentScopeTab): DocumentCatalogOption[] => {
      const personalRaw = t(
        `documents.catalog.scopes.${DocumentScope.Personal}.categories`,
        { ns: 'components', returnObjects: true },
      );
      const companyRaw = t(
        `documents.catalog.scopes.${DocumentScope.Company}.categories`,
        { ns: 'components', returnObjects: true },
      );
      const silverAlphaRaw = t(
        `documents.catalog.scopes.${DocumentScope.SilverAlpha}.categories`,
        { ns: 'components', returnObjects: true },
      );

      const personal = Array.isArray(personalRaw)
        ? (personalRaw as DocumentCategory[])
        : [];
      const company = Array.isArray(companyRaw)
        ? (companyRaw as DocumentCategory[])
        : [];
      const silverAlpha = Array.isArray(silverAlphaRaw)
        ? (silverAlphaRaw as DocumentCategory[])
        : [];

      const ids =
        scope === 'all'
          ? [...personal, ...company, ...silverAlpha]
              .filter(
                ({ id }, index, categories) =>
                  categories.findIndex(category => category.id === id) === index,
              )
              .map(({ id }) => id)
          : scope === DocumentScope.Company
          ? company.map(({ id }) => id)
          : scope === DocumentScope.SilverAlpha
          ? silverAlpha.map(({ id }) => id)
          : personal.map(({ id }) => id);

      return [...ids]
        .sort((a, b) => (a === 'other' ? 1 : b === 'other' ? -1 : 0))
        .map(id => ({ value: id, label: categoryLabels[id] ?? id }));
    },
    [categoryLabels, t],
  );

  const getSubcategoriesForCategory = useCallback(
    (scope: DocumentScopeTab, category: string): DocumentCatalogOption[] => {
      const personalRaw = t(
        `documents.catalog.scopes.${DocumentScope.Personal}.categories`,
        { ns: 'components', returnObjects: true },
      );
      const companyRaw = t(
        `documents.catalog.scopes.${DocumentScope.Company}.categories`,
        { ns: 'components', returnObjects: true },
      );
      const silverAlphaRaw = t(
        `documents.catalog.scopes.${DocumentScope.SilverAlpha}.categories`,
        { ns: 'components', returnObjects: true },
      );

      const personal = Array.isArray(personalRaw)
        ? (personalRaw as DocumentCategory[])
        : [];
      const company = Array.isArray(companyRaw)
        ? (companyRaw as DocumentCategory[])
        : [];
      const silverAlpha = Array.isArray(silverAlphaRaw)
        ? (silverAlphaRaw as DocumentCategory[])
        : [];

      const tree =
        scope === 'all'
          ? [...personal, ...company, ...silverAlpha].filter(
              ({ id }, index, categories) =>
                categories.findIndex(item => item.id === id) === index,
            )
          : scope === DocumentScope.Company
          ? company
          : scope === DocumentScope.SilverAlpha
          ? silverAlpha
          : personal;
      const ids = tree.find(({ id }) => id === category)?.subcategories ?? [];
      const safeIds = Array.isArray(ids) ? ids : [];

      return [...safeIds]
        .sort((a, b) => (a === 'other' ? 1 : b === 'other' ? -1 : 0))
        .map(id => ({ value: id, label: subcategoryLabels[id] ?? id }));
    },
    [subcategoryLabels, t],
  );

  const personalCategories = useMemo(
    () => getCategoriesForScope(DocumentScope.Personal),
    [getCategoriesForScope],
  );

  const companyCategories = useMemo(
    () => getCategoriesForScope(DocumentScope.Company),
    [getCategoriesForScope],
  );

  const currencies = useMemo(
    () =>
      Object.keys(currencyLabels).map(value => ({
        value,
        label: currencyLabels[value] ?? value,
      })),
    [currencyLabels],
  );

  const languages = useMemo(
    () =>
      Object.keys(languageLabels).map(value => ({
        value,
        label: languageLabels[value] ?? value,
      })),
    [languageLabels],
  );

  return {
    personalCategories,
    companyCategories,
    currencies,
    languages,
    getCategoriesForScope,
    getSubcategoriesForCategory,
    categoryLabels,
    subcategoryLabels,
    currencyLabels,
    languageLabels,
  };
};
